'use client';

/* eslint-disable react/jsx-props-no-spreading */
import { c as _c } from "react/compiler-runtime";
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../button';
import LoadingSpinner from '../../../loading-spinner';
import Popover from '../../../popover';
import ScrollArea from '../../../scroll-area';
import Text from '../../../text';
import type { FilterControl, FilterSelectedOption } from '../../filters.types';
import AddPill from './components/add-pill';
import ClearPill from './components/clear-pill';
import DateForm from './components/date-form';
import MultiSelectGroupedForm from './components/multi-select-grouped-form';
import SelectForm, { SelectFormKind } from './components/select-form';
import SelectedPill from './components/selected-pill';
import useDateOptions from './hooks/use-date-options';
import getDateLabel from './utils/get-date-label';
import getMultiSelectGroupedLabel from './utils/get-multi-select-grouped-label';
import getMultiSelectLabel from './utils/get-multi-select-label';
import getSingleSelectLabel from './utils/get-single-select-label';
export type ControlProps = {
  control: FilterControl;
  disabled?: boolean;
  onChange: (query: string, newSelectedOptions: FilterSelectedOption | FilterSelectedOption[]) => void;
  ariaLabel?: string;
};
const Control = t0 => {
  const $ = _c(50);
  const {
    control,
    disabled,
    onChange,
    ariaLabel
  } = t0;
  const {
    t
  } = useTranslation("ui");
  const [open, setOpen] = useState(false);
  const popoverId = useId();
  const dateOptions = useDateOptions();
  const {
    query,
    kind,
    label,
    loading,
    options,
    selectedOptions
  } = control;
  const hasSelectedOptions = selectedOptions && selectedOptions.length > 0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setOpen(false);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const close = t1;
  let t2;
  if ($[1] !== onChange || $[2] !== query) {
    t2 = () => {
      onChange(query, []);
    };
    $[1] = onChange;
    $[2] = query;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const clear = t2;
  let t3;
  if ($[4] !== onChange || $[5] !== query) {
    t3 = newSelectedOptions => {
      onChange(query, newSelectedOptions);
      setOpen(false);
    };
    $[4] = onChange;
    $[5] = query;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleSubmit = t3;
  let t4;
  if ($[7] !== label || $[8] !== t) {
    t4 = t("components.filters.filter-by", {
      label
    });
    $[7] = label;
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <header className="flex items-center justify-center p-2 border-b border-solid border-b-tertiary"><Text variant="label-2">{t4}</Text></header>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== handleSubmit || $[13] !== kind || $[14] !== label || $[15] !== loading || $[16] !== options || $[17] !== selectedOptions) {
    t6 = <ScrollArea padding={6} maxHeight="400px">{loading ? <LoadingSpinner color="secondary" ariaLabel={`Loading ${label}`} /> : <>{kind === "single-select" && <SelectForm kind={SelectFormKind.singleSelect} onSubmit={handleSubmit} options={options} selectedOptions={selectedOptions} />}{kind === "multi-select" && <SelectForm kind={SelectFormKind.multiSelect} onSubmit={handleSubmit} options={options} selectedOptions={selectedOptions} />}{kind === "multi-select-grouped" && <MultiSelectGroupedForm onSubmit={handleSubmit} options={options} selectedOptions={selectedOptions} />}{kind === "date" && <DateForm onSubmit={handleSubmit} selectedOptions={selectedOptions} />}</>}</ScrollArea>;
    $[12] = handleSubmit;
    $[13] = kind;
    $[14] = label;
    $[15] = loading;
    $[16] = options;
    $[17] = selectedOptions;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t) {
    t7 = t("components.filters.popover.cancel");
    $[19] = t;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const t8 = t7 as string;
  let t9;
  if ($[21] !== t8) {
    t9 = <Button onClick={close} variant="secondary">{t8}</Button>;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t) {
    t10 = t("components.filters.popover.apply");
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = t10 as string;
  let t12;
  if ($[25] !== t11) {
    t12 = <Button form="filter-form" type="submit" variant="primary">{t11}</Button>;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t9) {
    t13 = <footer className="flex items-center justify-end gap-3 p-3">{t9}{t12}</footer>;
    $[27] = t12;
    $[28] = t9;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13 || $[31] !== t5 || $[32] !== t6) {
    t14 = <div>{t5}{t6}{t13}</div>;
    $[30] = t13;
    $[31] = t5;
    $[32] = t6;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const popoverContent = t14;
  let t15;
  if ($[34] !== clear || $[35] !== dateOptions || $[36] !== disabled || $[37] !== hasSelectedOptions || $[38] !== kind || $[39] !== label || $[40] !== open || $[41] !== options || $[42] !== popoverId || $[43] !== selectedOptions) {
    t15 = <div className="flex flex-row">{hasSelectedOptions ? <div className="flex [&>button:first-of-type]:rounded-r-none [&>button:last-of-type]:rounded-l-none [&>button:last-of-type]:border-l-0"><ClearPill onClick={clear} disabled={disabled}>{label}</ClearPill><SelectedPill aria-controls={popoverId} aria-expanded={open} aria-haspopup="dialog" disabled={disabled}>{kind === "single-select" && getSingleSelectLabel(options, selectedOptions)}{kind === "multi-select" && getMultiSelectLabel(options, selectedOptions)}{kind === "multi-select-grouped" && getMultiSelectGroupedLabel(options, selectedOptions)}{kind === "date" && getDateLabel(dateOptions, selectedOptions)}</SelectedPill></div> : <AddPill aria-controls={popoverId} aria-expanded={open} aria-haspopup="dialog" disabled={disabled}>{label}</AddPill>}</div>;
    $[34] = clear;
    $[35] = dateOptions;
    $[36] = disabled;
    $[37] = hasSelectedOptions;
    $[38] = kind;
    $[39] = label;
    $[40] = open;
    $[41] = options;
    $[42] = popoverId;
    $[43] = selectedOptions;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== ariaLabel || $[46] !== open || $[47] !== popoverContent || $[48] !== t15) {
    t16 = <Popover content={popoverContent} side="bottom" align="start" aria-label={ariaLabel} controlledOpen={open} onControlledOpenChange={setOpen} onPointerDownOutside={close} onEscapeKeyDown={close}>{t15}</Popover>;
    $[45] = ariaLabel;
    $[46] = open;
    $[47] = popoverContent;
    $[48] = t15;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  return t16;
};
export default Control;